import * as endpoints from "../../lib/api/endpoints";
import * as error from "../../lib/api/error";
import * as routes from "../../constants/routes";
import * as validationLib from "../../lib/validation";
import ActionButton, {ActionButtonStyle} from "../ActionButton";
import BankAccountInfo from "../BankAccountInfo";
import callApplicationLead from "../../lib/callApplicationLead";
import CardAccountInfo from "../CardAccountInfo";
import checkImage from "../../images/check.svg";
import cn from "../../lib/classnames";
import ErrorBoundary from "../ErrorBoundary";
import Footer from "../Footer";
import FundLaterReminderModal from "../FundLaterReminderModal";
import getFirstInvalidKey, {ValidationType} from "../../lib/getFirstInvalidKey";
import getValidationKey from "../../lib/getValidationKey";
import hasFailingValidation from "../../lib/hasFailingValidation";
import Header from "../Header";
import IdleTimeoutWrapper from "../IdleTimeoutWrapper";
import isAddressKey from "../../lib/isAddressKey";
import isSecuredDepositKey from "../../lib/isSecuredDepositKey";
import Modal from "../Modal";
import RadioGroup from "../RadioGroup";
import React, {useEffect, useRef, useState} from "react";
import SetNoIndex from "../../lib/SetNoIndex";
import styles from "./SecuredCardDepositPage.module.css";
import SelectDeposit from "../SelectDeposit";
import Spinner from "../Spinner";
import useFocusInvalid from "../../lib/useFocusInvalid";
import ValidationList from "../ValidationList";
import validateDepositField from "../../lib/validateDepositField";
import {Column, Container, Row} from "../grid";
import {RadioOption} from "../RadioGroup/RadioGroup";
import {
    AddressKeys,
    AddressValidationKeys,
    CounterOffer,
    SecuredBankInfo,
    SecuredCardInfo,
    SecuredDepositKeys,
    SecuredDepositValidation,
    SecuredReminderKeys,
    SecuredReminderType,
} from "../../lib/api/models";
import {RouteComponentProps} from "react-router-dom";

const strings = {
    badRequest:
        "We were unable to find a secured card offer for this application. Please check your email for your secured card offer and follow the link to accept.",
    check: "check example",
    checkEmail: "Check your email for a message from 1st Financial Bank USA.",
    confirmIncorrect: "Confirm account number does not match account number",
    continue: "Continue",
    defaultError:
        "An error occurred. Please confirm your payment account information and try again.",
    defaultGenericString: "An error has occurred. Please double check the form and try again.",
    depositInfo:
        "To open a secured credit card account, you must make an initial deposit to your Security Deposit Account. The amount you deposit will be your initial credit limit.",
    errorModal: "Error Modal",
    finish: "Finish",
    makeDeposit: "Make a deposit from a Debit Card or Credit Card Account",
    makeDepositLater: "Make Deposit Later",
    makeMyDeposit: "Make My Deposit",
    otherErrors:
        "Something went wrong loading your secured card offer. Click the button below to try again.",
    selectDepositAmount: "Select a Deposit Amount",
    selectDescription: "This will be your credit limit.",
    selectLabel: "Deposit Amount",
    title: "1st Financial Bank USA Secured Card Deposit",
    tryAgain: "Try Again",
    unauthorized:
        "We don't have enough information to locate your secured card offer at this time. Please check your email for your secured card offer and follow the link to accept.",
    watch: "Watch for mail from 1st Financial Bank USA.",
};

const radioOptions: RadioOption[] = [
    {
        value: 0,
        label: "Make a deposit from a Checking or Savings Account",
    },
    {
        value: 1,
        label: "Make a deposit from a Debit Card or Credit Card Account",
    },
];

const EMPTY_BANK_INFO: SecuredBankInfo = {
    [SecuredDepositKeys.AccountNumber]: "",
    [SecuredDepositKeys.AbaNumber]: "",
};
const EMPTY_CARD_INFO: SecuredCardInfo = {
    [SecuredDepositKeys.CardNumber]: "",
    [SecuredDepositKeys.CardExpirationDate]: "",
    [SecuredDepositKeys.CardName]: "",
    [SecuredDepositKeys.CardType]: "",
    [SecuredDepositKeys.PhoneNumber]: "",
    [AddressKeys.AddressLine1]: "",
    [AddressKeys.AddressLine2]: "",
    [AddressKeys.City]: "",
    [AddressKeys.State]: "",
    [AddressKeys.Zipcode]: "",
};
const EMPTY_DEPOSIT_VALIDATION: SecuredDepositValidation = {};
const SECURED_BANK_OPTION = 0;
const SECURED_CARD_OPTION = 1;

function getDepositOptions(min: string, max: string, step: string): Array<string> {
    const minNum = parseInt(min, 10);
    const maxNum = parseInt(max, 10);
    const stepNum = parseInt(step, 10);
    const options: Array<string> = [];

    if (isNaN(minNum) || isNaN(maxNum) || isNaN(stepNum)) {
        return options;
    }
    for (let currNum = minNum; currNum <= maxNum; currNum += stepNum) {
        options.push(`$${currNum}`);
    }
    return options;
}

const EMPTY_COUNTER_OFFER: CounterOffer = {
    type: "",
    apr: -1,
    annualFee: -1,
    mpd: null,
    expDate: null,
    depositMin: "",
    depositMax: "",
    depositStep: "",
};

const UNAUTHORIZED = 401;
const BAD_REQUEST = 400;

function formatName(
    firstName: string | null,
    lastName: string | null,
    middleInitial: string | null,
) {
    let name = firstName ? firstName : "";
    if (middleInitial) {
        name += name.length > 0 ? ` ${middleInitial}.` : `${middleInitial}.`;
    }
    if (lastName) {
        name += name.length > 0 ? ` ${lastName}` : `${lastName}`;
    }
    return name;
}

export default function SecuredCardDepositPage(props: RouteComponentProps) {
    useEffect(() => {
        document.title = strings.title;
        callApplicationLead(`secured-deposit-option-selector`, null, window.location.href, "0");
    }, []);
    SetNoIndex();

    const windowUrl = window.location.href;
    const [counterOffer, setCounterOffer] = useState(EMPTY_COUNTER_OFFER);
    const [loadingApplication, setloadingApplication] = useState(true);
    const [errorMessage, setErrorMessage] = useState<string | null>("ERROR");
    const [nameOnAccount, setNameOnAccount] = useState("");
    const [emailOnAccount, setEmailOnAccount] = useState("");
    const [phoneOnAccount, setPhoneOnAccount] = useState("");
    const [econsent, setEconsent] = useState(false);

    function getApplication() {
        setloadingApplication(true);
        endpoints
            .getApplication()
            .then((application) => {
                setCounterOffer(
                    application.counterOffer ? application.counterOffer : EMPTY_COUNTER_OFFER,
                );
                setNameOnAccount(
                    formatName(
                        application.firstName,
                        application.lastName,
                        application.middleInitial,
                    ),
                );
                setEmailOnAccount(application.email ?? "");
                setPhoneOnAccount(application.homePhone ?? "");
                setEconsent(application.econsent);
                setErrorMessage(null);
            })
            .catch((err) => {
                const status: number = err.response.status;
                switch (status) {
                    case UNAUTHORIZED:
                        setErrorMessage(strings.unauthorized);
                        break;
                    case BAD_REQUEST:
                        setErrorMessage(strings.badRequest);
                        break;
                    default:
                        setErrorMessage(strings.otherErrors);
                }
                error.reportAxios(err, "SecuredCardDepositPage");
            })
            .finally(() => setloadingApplication(false));
    }

    useEffect(() => {
        getApplication();
    }, []);

    const options = getDepositOptions(
        counterOffer.depositMin,
        counterOffer.depositMax,
        counterOffer.depositStep,
    );

    const [amount, setAmount] = useState(-1);
    const [confirmAccountNumber, setConfirmAccountNumber] = useState("");
    const [isFundLaterModalOpen, setIsFundLaterModalOpen] = useState(false);
    const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
    const [loadingAccept, setLoadingAccept] = useState(false);
    const [loadingLater, setLoadingLater] = useState(false);
    const [radioValue, setRadioValue] = useState(0);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const [saveInfo, setSaveInfo] = useState(false);
    const [isWarrantChecked, setIsWarrantChecked] = useState(false);
    const [validation, setValidation] = useState<validationLib.Validation>({});
    const [validationCount, setValidationCount] = useState(1);
    const formValidation = validation.errors ?? EMPTY_DEPOSIT_VALIDATION;
    const fundLaterButtonRef = useRef(null);

    const isMountedRef = useRef<boolean>(false);
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const [bankInfo, setBankInfo] = useState({
        ...EMPTY_BANK_INFO,
    });
    const [cardInfo, setCardInfo] = useState({
        ...EMPTY_CARD_INFO,
    });

    /* Send application lead if field changed */
    const hasFieldChanged = {
        [SecuredDepositKeys.AccountNumber]: useRef(false),
        [SecuredDepositKeys.ConfirmAccountNumber]: useRef(false),
        [SecuredDepositKeys.AbaNumber]: useRef(false),
        [SecuredDepositKeys.CardNumber]: useRef(false),
        [SecuredDepositKeys.CardExpirationDate]: useRef(false),
        [SecuredDepositKeys.CardName]: useRef(false),
        [SecuredDepositKeys.CardType]: useRef(false),
        [SecuredDepositKeys.PhoneNumber]: useRef(false),
        [AddressKeys.AddressLine1]: useRef(false),
        [AddressKeys.AddressLine2]: useRef(false),
        [AddressKeys.City]: useRef(false),
        [AddressKeys.State]: useRef(false),
        [AddressKeys.Zipcode]: useRef(false),
        [SecuredDepositKeys.Save]: useRef(false),
        [SecuredDepositKeys.Warrant]: useRef(false),
        [SecuredDepositKeys.Amount]: useRef(false),
    };

    function clearAllValidation() {
        // Keep amount validation
        setValidation((prev) => {
            if (prev.errors && prev.errors[SecuredDepositKeys.Amount]) {
                const nextValidation = {
                    errors: {
                        [SecuredDepositKeys.Amount]: prev.errors[SecuredDepositKeys.Amount],
                    },
                };
                return nextValidation;
            }
            return {};
        });
    }

    function clearInfoAndValidation() {
        setBankInfo(EMPTY_BANK_INFO);
        setConfirmAccountNumber("");
        setSaveInfo(false);
        setIsWarrantChecked(false);
        setCardInfo(EMPTY_CARD_INFO);
        clearAllValidation();
    }

    function clearValidation(
        validationKey:
            | SecuredDepositKeys
            | SecuredReminderKeys
            | AddressKeys
            | AddressValidationKeys,
    ) {
        setValidation((prev) => {
            if (prev.generic || (prev.errors && prev.errors[validationKey])) {
                const nextValidation = {
                    ...prev,
                };
                delete nextValidation.generic;
                if (prev && prev.errors) {
                    delete prev.errors[validationKey];
                }
                return nextValidation;
            }
            return prev;
        });
    }

    function openErrorModal() {
        setIsErrorModalOpen(true);
        clearAllValidation();
    }

    function checkConfirmation(name: string, value: string) {
        if (
            isSecuredDepositKey(name) &&
            (name === SecuredDepositKeys.ConfirmAccountNumber ||
                name === SecuredDepositKeys.AccountNumber) &&
            (value !== bankInfo[SecuredDepositKeys.AccountNumber] || value !== confirmAccountNumber)
        ) {
            setValidation((prev) => ({
                ...prev,
                errors: {
                    ...prev?.errors,
                    [SecuredDepositKeys.ConfirmAccountNumber]: [strings.confirmIncorrect],
                },
            }));
        }
    }

    function onBlurInput(e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>) {
        const value = e.currentTarget.value;
        const name = e.currentTarget.name;

        if (isSecuredDepositKey(name) || isAddressKey(name)) {
            if (hasFieldChanged[name].current) {
                callApplicationLead(`secured-deposit-${name}`, null, windowUrl, value);
                hasFieldChanged[name].current = false;
            }
            const {isValid, messages, validationKey} = validateDepositField(name, value);
            if (isValid) {
                checkConfirmation(name, value);
                return;
            }

            if (validationKey !== null) {
                setValidation((prev) => ({
                    ...prev,
                    errors: {
                        ...prev?.errors,
                        [validationKey]: messages,
                    },
                }));
            }
        }
        checkConfirmation(name, value);
    }

    function formatDepositString(depositAmount: string) {
        const formattedAmount = depositAmount.replace("$", "");
        const numToReturn = parseInt(formattedAmount, 10);
        if (isNaN(numToReturn)) {
            return -1;
        }
        return numToReturn;
    }

    function onChangeBankInfo(name: SecuredDepositKeys, value: string) {
        setBankInfo((prevInfo) => ({
            ...prevInfo,
            [name]: value,
        }));
        if (name === SecuredDepositKeys.AccountNumber) {
            clearValidation(SecuredDepositKeys.ConfirmAccountNumber);
        }
        hasFieldChanged[name].current = true;
        clearValidation(name);
    }

    function onChangeCardInfo(name: SecuredDepositKeys | AddressKeys, value: string) {
        setCardInfo((prevInfo) => ({
            ...prevInfo,
            [name]: value,
        }));
        hasFieldChanged[name].current = true;
        if (isAddressKey(name)) {
            const validationKey = getValidationKey(name as AddressKeys);
            clearValidation(validationKey);
        } else {
            clearValidation(name);
        }
    }

    function onChangeAddress(field: AddressKeys, value: string) {
        onChangeCardInfo(field, value);
    }

    function onSubmit(e: React.FormEvent) {
        e.preventDefault();
        checkConfirmation(
            SecuredDepositKeys.AccountNumber,
            bankInfo[SecuredDepositKeys.AccountNumber],
        );
        if (hasFailingValidation(validation)) {
            /* Focus on any failing validation w/o having to use required */
            setValidationCount((prev) => prev + 1);
            return;
        }
        setLoadingAccept(true);
        const apiCall =
            radioValue === SECURED_BANK_OPTION
                ? endpoints.securedBankDeposit(amount, saveInfo, bankInfo)
                : endpoints.securedCardDeposit(amount, saveInfo, cardInfo);
        apiCall
            .then(() => {
                if (!isMountedRef.current) {
                    return;
                }
                props.history.replace(`${routes.REFERRAL_PAGE}${window.location.search}`, {
                    isSecured: true,
                });
            })
            .catch((err) => {
                if (!isMountedRef.current) {
                    return;
                }
                // Validation lib adds default generic if there is none present. Check if err body empty manually
                const errValidation = validationLib.getValidationObj(err);
                const status: number = err.response.status;
                if (
                    status !== BAD_REQUEST ||
                    (!err.response.data.generic && !err.response.data.errors)
                ) {
                    openErrorModal();
                } else {
                    setValidation(errValidation);
                    setValidationCount((prev) => prev + 1);
                }
            })
            .finally(() => setLoadingAccept(false));
    }

    function onSubmitFundLater(remindDate: Date, depositIndex: number, type: SecuredReminderType) {
        setIsFundLaterModalOpen(false);
        setLoadingLater(true);
        clearValidation(SecuredReminderKeys.Date);
        clearValidation(SecuredReminderKeys.Amount);
        endpoints
            .securedCardReminder({
                [SecuredReminderKeys.Type]: type,
                [SecuredReminderKeys.Date]: remindDate.toISOString().split("T")[0],
                [SecuredReminderKeys.Email]: emailOnAccount,
                [SecuredReminderKeys.Phone]: phoneOnAccount,
                [SecuredReminderKeys.Amount]: formatDepositString(options[depositIndex]),
            })
            .then(() => {
                if (!isMountedRef.current) {
                    return;
                }
                props.history.replace(`${routes.SECURED_FUND_LATER_SUBMITTED}`);
            })
            .catch((err) => {
                if (!isMountedRef.current) {
                    return;
                }
                const errValidation = validationLib.getValidationObj(err);
                const status: number = err.response.status;
                if (
                    status !== BAD_REQUEST ||
                    (!err.response.data.generic && !err.response.data.errors)
                ) {
                    openErrorModal();
                } else {
                    setValidation(errValidation);
                    setValidationCount((prev) => prev + 1);
                }
            })
            .finally(() => setLoadingLater(false));
    }

    /* Focus first failing validation */
    const amountRef = useRef<HTMLSelectElement>(null);
    const submitBtnRef = useRef(null);

    const firstInvalidKey = getFirstInvalidKey(
        validation,
        radioValue === SECURED_BANK_OPTION
            ? ValidationType.SecuredBankDeposit
            : ValidationType.SecuredCardDeposit,
    );
    const firstInvalidRef = firstInvalidKey === SecuredDepositKeys.Amount ? amountRef : undefined;
    useFocusInvalid(validationCount, firstInvalidRef?.current);

    function renderAccountInfo() {
        return radioValue === SECURED_CARD_OPTION ? (
            <Column
                largeOffset={6}
                large={5}
                mediumOffset={4}
                medium={3}
                className={styles.container}
            >
                <CardAccountInfo
                    className={styles.radioForm}
                    onBlur={onBlurInput}
                    onChange={onChangeCardInfo}
                    cardName={cardInfo[SecuredDepositKeys.CardName]}
                    line1={cardInfo[AddressKeys.AddressLine1]}
                    line2={cardInfo[AddressKeys.AddressLine2]}
                    city={cardInfo[AddressKeys.City]}
                    state={cardInfo[AddressKeys.State]}
                    zipcode={cardInfo[AddressKeys.Zipcode]}
                    onChangeAddress={onChangeAddress}
                    cardNumber={cardInfo[SecuredDepositKeys.CardNumber]}
                    expirationDate={cardInfo[SecuredDepositKeys.CardExpirationDate]}
                    phoneNumber={cardInfo[SecuredDepositKeys.PhoneNumber]}
                    isChecked={saveInfo}
                    setIsChecked={(value) => {
                        callApplicationLead(
                            `secured-deposit-card-${SecuredDepositKeys.Save}`,
                            null,
                            windowUrl,
                            value.toString(),
                        );
                        setSaveInfo(value);
                    }}
                    isWarrantChecked={isWarrantChecked}
                    setIsWarrantChecked={(value) => {
                        callApplicationLead(
                            `secured-deposit-card-${SecuredDepositKeys.Warrant}`,
                            null,
                            windowUrl,
                            value.toString(),
                        );
                        setIsWarrantChecked(value);
                    }}
                    firstInvalidKey={firstInvalidKey}
                    validationCount={validationCount}
                    validationCardName={formValidation[SecuredDepositKeys.CardName]}
                    validationCardType={formValidation[SecuredDepositKeys.CardType]}
                    validationLine1={formValidation[AddressValidationKeys.AddressLine1]}
                    validationLine2={formValidation[AddressValidationKeys.AddressLine2]}
                    validationCity={formValidation[AddressValidationKeys.City]}
                    validationState={formValidation[AddressValidationKeys.State]}
                    validationZipcode={formValidation[AddressValidationKeys.Zipcode]}
                    validationCardNumber={formValidation[SecuredDepositKeys.CardNumber]}
                    validationExpirationDate={formValidation[SecuredDepositKeys.CardExpirationDate]}
                    validationPhoneNumber={formValidation[SecuredDepositKeys.PhoneNumber]}
                />
            </Column>
        ) : null;
    }

    function renderError() {
        return (
            <Row>
                <Column
                    largeOffset={1}
                    large={10}
                    mediumOffset={1}
                    medium={6}
                    className={styles.container}
                >
                    <div className={styles.somethingWentWrong}>{errorMessage}</div>
                    <ActionButton
                        style={ActionButtonStyle.primary}
                        onClick={getApplication}
                        label={strings.tryAgain}
                        type="button"
                    />
                </Column>
            </Row>
        );
    }

    function renderValidation() {
        return validation.generic ? (
            <Row>
                <Column
                    largeOffset={1}
                    large={10}
                    mediumOffset={1}
                    medium={6}
                    className={styles.container}
                >
                    <div className={styles.validation}>
                        <ValidationList
                            className={styles.validation}
                            messages={validation.generic}
                        />
                    </div>
                </Column>
            </Row>
        ) : null;
    }

    function renderDepositScreen() {
        let modalErrors: string[] = [];
        if (validation.errors && validation.errors[SecuredReminderKeys.Date]) {
            modalErrors = [...modalErrors, ...validation.errors[SecuredReminderKeys.Date]];
        }
        if (validation.errors && validation.errors[SecuredReminderKeys.Amount]) {
            modalErrors = [...modalErrors, ...validation.errors[SecuredReminderKeys.Amount]];
        }

        return errorMessage ? (
            renderError()
        ) : (
            <IdleTimeoutWrapper
                focusAfterContinue={null}
                noLink={true}
                description={econsent ? strings.checkEmail : strings.watch}
            >
                <Row>
                    <Column
                        largeOffset={1}
                        large={10}
                        mediumOffset={1}
                        medium={6}
                        className={styles.container}
                    >
                        <div className={styles.makeMyDeposit}>{strings.makeMyDeposit}</div>
                        <div className={styles.depositInfo}>{strings.depositInfo}</div>
                    </Column>
                </Row>
                <RadioGroup
                    radioInputs={radioOptions}
                    selectedValue={radioValue}
                    setValue={(n) => {
                        callApplicationLead(
                            "secured-deposit-option-selector",
                            null,
                            windowUrl,
                            n.toString(),
                        );
                        setRadioValue(n);
                        clearInfoAndValidation();
                    }}
                    columnClassName={cn(
                        styles.container,
                        styles.radioGroup,
                        radioValue === -1 ? styles.radioGroupMargin : undefined,
                    )}
                    loading={loadingAccept || loadingLater}
                />
                <Row>
                    {radioValue === SECURED_BANK_OPTION ? (
                        <>
                            <Column
                                largeOffset={1}
                                large={5}
                                mediumOffset={1}
                                medium={3}
                                className={styles.container}
                            >
                                <BankAccountInfo
                                    className={styles.radioForm}
                                    onBlur={onBlurInput}
                                    name={nameOnAccount}
                                    onChange={onChangeBankInfo}
                                    abaNumber={bankInfo[SecuredDepositKeys.AbaNumber]}
                                    accountNumber={bankInfo[SecuredDepositKeys.AccountNumber]}
                                    confirmAccountNumber={confirmAccountNumber}
                                    setConfirmAccountNumber={(value) => {
                                        clearValidation(SecuredDepositKeys.ConfirmAccountNumber);
                                        setConfirmAccountNumber(value);
                                        hasFieldChanged[
                                            SecuredDepositKeys.ConfirmAccountNumber
                                        ].current = true;
                                    }}
                                    isChecked={saveInfo}
                                    setIsChecked={(value) => {
                                        callApplicationLead(
                                            `secured-deposit-bank-${SecuredDepositKeys.Save}`,
                                            null,
                                            windowUrl,
                                            value.toString(),
                                        );
                                        setSaveInfo(value);
                                    }}
                                    isWarrantChecked={isWarrantChecked}
                                    setIsWarrantChecked={(value) => {
                                        callApplicationLead(
                                            `secured-deposit-bank-${SecuredDepositKeys.Warrant}`,
                                            null,
                                            windowUrl,
                                            value.toString(),
                                        );
                                        setIsWarrantChecked(value);
                                    }}
                                    firstInvalidKey={firstInvalidKey}
                                    validationCount={validationCount}
                                    validationAbaNumber={
                                        formValidation[SecuredDepositKeys.AbaNumber]
                                    }
                                    validationAccountNumber={
                                        formValidation[SecuredDepositKeys.AccountNumber]
                                    }
                                    validationConfirmAccountNumber={
                                        formValidation[SecuredDepositKeys.ConfirmAccountNumber]
                                    }
                                />
                            </Column>
                            <Column large={5} medium={3} className={styles.container}>
                                <div className={styles.checkContainer}>
                                    <img
                                        className={styles.checkImage}
                                        src={checkImage}
                                        alt={strings.check}
                                    />
                                </div>
                            </Column>
                        </>
                    ) : (
                        renderAccountInfo()
                    )}
                </Row>
                <Row>
                    <Column
                        largeOffset={1}
                        large={10}
                        mediumOffset={1}
                        medium={6}
                        className={styles.container}
                    >
                        <div className={styles.depositContainer}>
                            <div className={cn(styles.leftBlock, styles.leftLabel)}>
                                {strings.selectDepositAmount}
                            </div>
                            <div className={cn(styles.rightBlock, styles.selectDescription)}>
                                <SelectDeposit
                                    id="deposit-amount"
                                    label={strings.selectLabel}
                                    name={SecuredDepositKeys.Amount}
                                    onBlur={onBlurInput}
                                    options={options}
                                    ref={amountRef}
                                    required={true}
                                    selectedIndex={selectedIndex}
                                    setSelected={(index: number, option: string) => {
                                        const formattedOption = formatDepositString(option);
                                        callApplicationLead(
                                            "secured-deposit-amount",
                                            null,
                                            windowUrl,
                                            option,
                                        );
                                        setSelectedIndex(index);
                                        setAmount(formattedOption);
                                        clearValidation(SecuredDepositKeys.Amount);
                                    }}
                                    validationMessage={formValidation[SecuredDepositKeys.Amount]}
                                />
                                <div className={styles.selectDescription}>
                                    {strings.selectDescription}
                                </div>
                            </div>
                        </div>
                    </Column>
                </Row>
                {modalErrors && modalErrors.length ? (
                    <Row>
                        <Column
                            largeOffset={1}
                            large={10}
                            mediumOffset={1}
                            medium={6}
                            className={styles.container}
                        >
                            <div className={styles.validation}>
                                <ValidationList
                                    className={styles.validation}
                                    messages={modalErrors}
                                />
                            </div>
                        </Column>
                    </Row>
                ) : null}
                <Row>
                    <Column
                        largeOffset={2}
                        large={9}
                        mediumOffset={1}
                        medium={6}
                        className={styles.container}
                    >
                        <div
                            className={cn(
                                styles.buttonColumn,
                                styles.buttonContainer,
                                modalErrors && modalErrors.length ? undefined : styles.buttonMargin,
                            )}
                        >
                            <ActionButton
                                style={ActionButtonStyle.outlined}
                                label={strings.makeDepositLater}
                                fullWidth={true}
                                className={styles.laterButton}
                                loading={loadingLater}
                                disabled={loadingAccept}
                                onClick={() => {
                                    callApplicationLead(
                                        "secured-deposit-make-deposit-later",
                                        null,
                                        windowUrl,
                                    );
                                    setIsFundLaterModalOpen(true);
                                }}
                                type="button"
                            />
                            <ActionButton
                                style={ActionButtonStyle.primary}
                                label={strings.finish}
                                fullWidth={true}
                                className={styles.finishButton}
                                disabled={loadingLater || hasFailingValidation(validation)}
                                loading={loadingAccept}
                                onClick={() =>
                                    callApplicationLead("secured-deposit-finish", null, windowUrl)
                                }
                                ref={submitBtnRef}
                                type="submit"
                            />
                        </div>
                    </Column>
                </Row>
                {renderValidation()}
            </IdleTimeoutWrapper>
        );
    }

    return (
        <ErrorBoundary>
            <Header />
            <form onSubmit={onSubmit}>
                <Container
                    className={cn(
                        styles.topContainer,
                        loadingApplication ? styles.loadingApplication : undefined,
                    )}
                >
                    {!loadingApplication ? (
                        renderDepositScreen()
                    ) : (
                        <div className={styles.loadingContainer}>
                            <Spinner isVisible={true} className={styles.loaderAppearance} />
                        </div>
                    )}
                </Container>
            </form>
            <Footer />
            <FundLaterReminderModal
                isOpen={isFundLaterModalOpen}
                onClose={() => setIsFundLaterModalOpen(false)}
                onSubmit={onSubmitFundLater}
                triggerElement={fundLaterButtonRef.current}
                email={emailOnAccount}
                lastFourPhoneDigits={phoneOnAccount.substring(6)}
                depositOptions={options}
            />
            <Modal
                id="errorModal"
                label={strings.errorModal}
                isOpen={isErrorModalOpen}
                onClose={() => setIsErrorModalOpen(false)}
                onSubmit={() => setIsErrorModalOpen(false)}
                onSubmitLabel={strings.continue}
                triggerElement={submitBtnRef?.current}
            >
                <div className={styles.modalText}>{strings.defaultError}</div>
            </Modal>
        </ErrorBoundary>
    );
}
